export interface ParsedLocation {
  city: string;
  state: string;
  country: string;
}

const COUNTRY_NORMALIZATIONS: Record<string, string> = {
  'usa': 'United States',
  'us': 'United States',
  'u.s.': 'United States',
  'u.s.a.': 'United States',
  'united states': 'United States',
  'united states of america': 'United States',
  'uk': 'United Kingdom',
  'u.k.': 'United Kingdom',
  'united kingdom': 'United Kingdom',
  'england': 'United Kingdom',
  'great britain': 'United Kingdom',
  'south korea': 'South Korea',
  'republic of korea': 'South Korea',
  'uae': 'United Arab Emirates',
  'u.a.e.': 'United Arab Emirates',
};

const STATE_ABBREVIATIONS: Record<string, string> = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas',
  'CA': 'California', 'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware',
  'FL': 'Florida', 'GA': 'Georgia', 'HI': 'Hawaii', 'ID': 'Idaho',
  'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa', 'KS': 'Kansas',
  'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi',
  'MO': 'Missouri', 'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada',
  'NH': 'New Hampshire', 'NJ': 'New Jersey', 'NM': 'New Mexico', 'NY': 'New York',
  'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio', 'OK': 'Oklahoma',
  'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah',
  'VT': 'Vermont', 'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia',
  'WI': 'Wisconsin', 'WY': 'Wyoming', 'DC': 'District of Columbia',
};

const US_STATES = new Set([
  ...Object.keys(STATE_ABBREVIATIONS),
  ...Object.values(STATE_ABBREVIATIONS),
]);

export function normalizeCountry(country: string): string {
  const trimmed = country.trim();
  const lower = trimmed.toLowerCase();
  return COUNTRY_NORMALIZATIONS[lower] || trimmed;
}

export function normalizeState(state: string): string {
  const trimmed = state.trim();
  return STATE_ABBREVIATIONS[trimmed] || trimmed;
}

// Values that are not real country/city/state names
const INVALID_LOCATION_VALUES = new Set([
  'multiple', 'multiple locations', 'multiple manufacturers',
  'multiple (global manufacturer)', 'multiple manufacturers (generic drug)',
  'international operations', 'various', 'worldwide', 'global',
  'n/a', 'unknown', 'tbd', 'not applicable', 'caribbean',
]);

export function parseHeadquarters(headquarters: string): ParsedLocation {
  if (!headquarters) return { city: '', state: '', country: '' };

  // Clean up: remove quotes
  let clean = headquarters.replace(/^['"]|['"]$/g, '').trim();

  // Skip obviously invalid values
  if (INVALID_LOCATION_VALUES.has(clean.toLowerCase())) {
    return { city: '', state: '', country: '' };
  }

  // Handle multi-location: take the first (primary) one
  if (clean.includes(';')) {
    clean = clean.split(';')[0].trim();
  }

  // Remove parenthetical annotations like "(primary)" or "(operational)"
  clean = clean.replace(/\s*\([^)]*\)\s*/g, '').trim();

  const parts = clean.split(',').map(p => p.trim()).filter(p => p);

  // Filter out parts that look like street addresses
  const locationParts = parts.filter(p => {
    if (/^\d/.test(p)) return false;
    if (/\b(Street|St\.|Avenue|Ave\.|Boulevard|Blvd\.|Drive|Dr\.|Road|Rd\.|Way|Lane|Ln\.|Place|Pl\.)\b/i.test(p)) return false;
    return true;
  });

  if (locationParts.length === 0) return { city: '', state: '', country: '' };

  if (locationParts.length === 1) {
    const val = locationParts[0];
    const normalized = normalizeCountry(val);
    if (INVALID_LOCATION_VALUES.has(normalized.toLowerCase())) {
      return { city: '', state: '', country: '' };
    }
    return { city: val, state: '', country: normalized };
  }

  if (locationParts.length === 2) {
    const [first, second] = locationParts;
    if (US_STATES.has(second) || STATE_ABBREVIATIONS[second]) {
      return {
        city: first,
        state: normalizeState(second),
        country: 'United States',
      };
    }
    const normalizedCountry = normalizeCountry(second);
    if (INVALID_LOCATION_VALUES.has(normalizedCountry.toLowerCase())) {
      return { city: first, state: '', country: '' };
    }
    return { city: first, state: '', country: normalizedCountry };
  }

  // 3+ parts: City, State/Province, Country
  const city = locationParts[0];
  const state = locationParts[locationParts.length - 2];
  const country = locationParts[locationParts.length - 1];
  const normalizedCountry = normalizeCountry(country);

  if (INVALID_LOCATION_VALUES.has(normalizedCountry.toLowerCase())) {
    return { city, state: normalizeState(state), country: '' };
  }

  return {
    city,
    state: normalizeState(state),
    country: normalizedCountry,
  };
}

export function extractCountry(headquarters: string): string {
  return parseHeadquarters(headquarters).country;
}

export function extractUniqueCountries(items: { headquarters: string }[]): string[] {
  const countrySet = new Set<string>();
  for (const item of items) {
    if (item.headquarters) {
      const country = extractCountry(item.headquarters);
      if (country) countrySet.add(country);
    }
  }
  return Array.from(countrySet).sort();
}

export function getTopItems(items: string[], limit: number): { name: string; count: number }[] {
  const counts: Record<string, number> = {};
  for (const item of items) {
    if (item) {
      counts[item] = (counts[item] || 0) + 1;
    }
  }
  return Object.entries(counts)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}

export function formatTagName(tag: string): string {
  return tag
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
